import {
  AccessContext,
  RemoteDataSource,
  Required,
  Schema,
  TextField
} from "lego-gui-lib/lib";
import { NotApplicable } from "lego-gui-lib/lib/components/Inputs/NotApplicable";
import { createContext } from "react";
import { api, mainStore } from "../../core/mainStore";
import { CustomerFK } from "../customer/schema";
import { t } from "../../core/i18n/i18n";
import { EnumField, ForeignKeyField, Type } from "lego-gui-lib";
import {
  BooleanField,
  VisibilityEnum,
  VisibilityType,
  withVisibility
} from "../../common/common";
import { get } from "lodash";
import { FormState } from "final-form";
import { Auth } from "../../core/model/auth";
import { ContextAwareListDataSource } from "../../common/ContextAwareListDataSource";
import { ForeignKeyFieldWithPageSize } from "../../common/ForeignKeyFieldWithPageSize";

const auth = mainStore.auth;

export enum MediaType {
  ANNOUNCEMENT = 0,
  SMS = 1
}

export function MediaTypeField(): Type {
  return EnumField(
    [
      { label: t("hns:message:mediaType.sms"), value: MediaType.SMS },
      {
        label: t("hns:message:mediaType.announcement"),
        value: MediaType.ANNOUNCEMENT
      }
    ],
    Required
  );
}

const onVisibilityChange = (
  newValue: any,
  oldValue: any,
  formState: FormState<any>
) => {
  if (newValue === VisibilityType.PRIVATE) {
    formState.values["charged"] = 1;
  } else {
    formState.values["customerRef"] = null;
  }
};

export const MessageSchema = new Schema({
  primaryKey: ["id"],
  defaultSort: { sort: "visibility", order: "desc" },
  security: {
    readableRole: Auth.Roles.ANYONE,
    editableRole: Auth.Roles.ANYONE_WITH_WRITE
  },
  fields: [
    {
      name: "name",
      type: TextField(Required),
      title: t("common:name")
    },
    {
      name: "visibility",
      type: VisibilityEnum(),
      title: t("common:visibility"),
      readonly: () => !auth.isAdmin(),
      onChange: onVisibilityChange
    },
    {
      name: "customerRef",
      type: CustomerFK,
      title: t("hns:customer:domain"),
      if: (ctx: AccessContext) =>
        auth.isAdmin() && get(ctx, "data.visibility") === VisibilityType.PRIVATE
    },
    {
      name: "mediaType",
      type: MediaTypeField(),
      title: t("hns:message:mediaType")
    },
    {
      name: "charged",
      type: NotApplicable(
        BooleanField(Required),
        (ctx: AccessContext) =>
          get(ctx, "data.mediaType") !== MediaType.ANNOUNCEMENT
      ),
      if: () => auth.isAdmin(),
      title: t("hns:message:charged")
    },
    {
      name: "description",
      type: TextField(),
      title: t("common:description")
    }
  ]
});

export const CreateMessageSchema = MessageSchema.extend({
  charged: {
    if: (ctx: AccessContext) =>
      auth.isAdmin() && get(ctx, "data.mediaType") === MediaType.ANNOUNCEMENT
  }
});

export const EditMessageSchema = MessageSchema.extend({
  // @ts-ignore
  mediaType: { readonly: true },
  // @ts-ignore
  visibility: { readonly: true },
  // @ts-ignore
  customerRef: { readonly: true },
  charged: {
    if: (ctx: AccessContext) =>
      auth.isAdmin() && get(ctx, "data.mediaType") === MediaType.ANNOUNCEMENT
  }
});

mainStore.message = RemoteDataSource.create({
  api: api,
  name: "message",
  schema: MessageSchema,
  url: "/messages",
  commons: mainStore.commons
});

export const messageContext = createContext(mainStore.message);

mainStore.messageFk = RemoteDataSource.clone(mainStore.message);

export const MessageFk = ForeignKeyField(
  {
    dataSource: mainStore.messageFk.listDataSource,
    labelField: withVisibility(),
    searchField: "name",
    valueField: "id"
  },
  Required
);

mainStore.customerMessages = new ContextAwareListDataSource({
  api: api,
  name: "message",
  schema: MessageSchema,
  url: "/messages",
  commons: mainStore.commons,
  observableFieldRef: "customerRef",
  observableDataSource: "customer"
});

export const customerMessagesContext = createContext(
  mainStore.customerMessages
);

mainStore.customerMessagesAnnouncements = ContextAwareListDataSource.clone(
  mainStore.customerMessages,
  {
    params: {
      filters: [
        {
          field: "mediaType",
          value: MediaType.ANNOUNCEMENT.toString()
        }
      ]
    }
  }
);

export const CustomerMessagesFK = ForeignKeyFieldWithPageSize(
  {
    dataSource: mainStore.customerMessages,
    labelField: withVisibility(),
    searchField: "name",
    valueField: "id",
    pageSize: 10000
  },
  Required
);

export const CustomerMessagesAnnouncementFK = ForeignKeyField(
  {
    dataSource: mainStore.customerMessagesAnnouncements,
    labelField: withVisibility(),
    searchField: "name",
    valueField: "id"
  },
  Required
);

export const CustomerMessagesAnnouncementFKNotRequired = ForeignKeyField({
  dataSource: mainStore.customerMessagesAnnouncements,
  labelField: withVisibility(),
  searchField: "name",
  valueField: "id",
  placeholder: "Default message"
});

export const CustomerMessagesFKNotRequired = ForeignKeyField({
  dataSource: mainStore.customerMessages,
  labelField: withVisibility(),
  searchField: "name",
  valueField: "id"
});

export const NumericCodeCustomerMessageFK = ForeignKeyField({
  dataSource: mainStore.customerMessages,
  labelField: withVisibility(),
  searchField: "name",
  valueField: "id",
  placeholder: "Default message"
});
